import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Menu, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { AdvertStatus } from 'enums'

import { Button, ButtonWithIcon, SectionEditButton } from 'components/buttons'
import { PrintCVButtons } from 'components/Print'
import UserImage from 'components/UserImage'
import {
  IconArrow,
  IconCheck,
  IconChevron,
  IconClose,
  IconEdit,
  IconMessages,
  IconRelocation
} from 'icons'
import IconCalendar from 'icons/IconCalendar'

import useMessageStore from 'store/useMessageStore'
import { useUpdateAdvertMatchStatus } from 'hooks/useMatching'
import { useSaveCv } from 'hooks/useSavedCVs'

import { formatAddress } from 'utils/address.util'
import { handleQueryError } from 'utils/queryErrorHandler'

import { MatchPercentage, PhoneNumber } from './components'
import ExpandableSkills from './ExpandableSkills'
import { ICVHeaderProps } from './types'

import 'swiper/css'

const CVHeader = ({
  candidateId,
  avatarUrl,
  firstName,
  lastName,
  country,
  city,
  phone,
  skills,
  drivingLicence,
  openToRelocation,
  expectedSalaryEuros,
  remoteWorkType,
  employmentStatus,
  contact = true,
  isRejected,
  print,
  editable,
  advertId,
  redirectToMessage,
  matchPercentage,
  isSaved,
  rejectCallback,
  saveCallback,
  onEditAvatar,
  onEditBasic,
  onEditSkills,
  onEditPreferences
}: ICVHeaderProps) => {
  const router = useRouter()
  const { t } = useTranslation(['actions', 'cv_modal', 'crm', 'enums'])

  const setOpenChatConfig = useMessageStore((state) => state.setOpenChatConfig)

  const [isMatchRejected, setIsMatchRejected] = useState(isRejected ?? false)

  useEffect(() => {
    setIsMatchRejected(isRejected ?? false)
  }, [isRejected])

  const { mutate } = useUpdateAdvertMatchStatus({
    onSuccess: (data, variables, context) => {
      toast.success(t('project.rejected', { ns: 'crm' }))
      setIsMatchRejected(true)
      rejectCallback?.(data, variables, context)
    },
    onError: handleQueryError
  })

  const { mutate: saveCv, isLoading } = useSaveCv({ onSuccess: saveCallback })
  return (
    <div className="gradient-gray mx-auto w-full border-b-[1px] border-b-gray-100 print:pt-2">
      {candidateId && <PrintCVButtons cvId={candidateId} />}
      <div className="flex h-full w-full flex-col items-start justify-end gap-x-12 gap-y-4 px-5 py-12 print:relative print:flex-row print:items-start print:justify-start md:h-fit md:flex-row md:justify-between md:px-20">
        <div className="flex w-full items-center justify-between md:w-auto">
          <div className="relative" onClick={onEditAvatar}>
            <div className="relative h-52 w-52 overflow-hidden rounded-full border border-gray-200 print:h-28 print:w-28 md:h-48 md:w-48">
              <UserImage
                key={avatarUrl}
                src={avatarUrl}
                alt={`${firstName ?? ''} user avatar`}
                className="h-52 w-52 print:h-28 print:w-28 md:h-48 md:w-48"
              />
            </div>
            <div className="absolute bottom-[-4px] mx-auto flex w-full items-center justify-center">
              <span
                onClick={onEditAvatar}
                className={clsx(
                  'flex cursor-default items-center justify-center rounded-full bg-white py-1 pl-3 pr-4 text-center text-xs font-bold uppercase shadow-sm',
                  editable &&
                    'cursor-pointer transition-opacity hover:opacity-80 active:opacity-60'
                )}
              >
                <span className="ml-[-2px] h-6 w-6">
                  {editable ? (
                    <IconEdit className="mt-[5px] ml-1 h-4 w-4" />
                  ) : (
                    <IconCheck className="mt-[1px]" />
                  )}
                </span>
                <span>
                  {editable
                    ? t('edit', { ns: 'actions' })
                    : t('active', { ns: 'actions' })}
                </span>
              </span>
            </div>
          </div>

          {!!matchPercentage && (
            <MatchPercentage
              matchPercentage={matchPercentage}
              className={'flex text-3xl font-bold md:hidden md:text-4xl'}
            />
          )}
        </div>

        <div className="flex flex-col items-start justify-start md:flex-1">
          <div className="flex-0 mb-2 flex w-full items-start justify-between md:mb-8 md:flex-1">
            <div className="w-full text-4xl font-bold md:text-4xl">
              <div className="flex justify-between">
                <h2 className="my-4 max-w-[210px] break-normal md:max-w-[300px] lg:max-w-[410px]">
                  {firstName} {lastName}
                </h2>

                {!!matchPercentage && (
                  <MatchPercentage
                    matchPercentage={matchPercentage}
                    className={'hidden md:flex'}
                  />
                )}
              </div>

              <div className="mb-2 flex items-center text-2xl font-light md:mb-0 md:text-lg">
                {formatAddress({
                  country: country,
                  city: city,
                  lang: router.locale,
                  reverse: true
                })}
                {openToRelocation && (
                  <span
                    className={
                      'ml-2 flex w-fit rounded-full border border-gray-300 py-1.5 px-2.5 text-sm'
                    }
                  >
                    <IconRelocation className="mr-1 h-5 w-5" />
                    {t('fields.prefRelocation', { ns: 'cv_modal' })}
                  </span>
                )}
              </div>
              <PhoneNumber phone={phone} />
            </div>
            <div>
              {editable && (
                <SectionEditButton
                  data-cy="personal-information-modal"
                  onClick={onEditBasic}
                />
              )}
            </div>
          </div>
          <div className={clsx('mb-4', editable && 'flex items-start gap-4')}>
            <ExpandableSkills
              isPure
              skills={skills?.filter((x) => x.order) ?? []}
              addSkill={
                drivingLicence ? (
                  <span>
                    <span className="text-md mr-2">🚗</span>{' '}
                    {t('fields.drivingLicense', { ns: 'cv_modal' })}
                  </span>
                ) : null
              }
            />
            {editable && <SectionEditButton isPlain onClick={onEditSkills} />}
          </div>
          <div className="flex items-center gap-x-5 ">
            <div className="flex flex-col">
              {expectedSalaryEuros && expectedSalaryEuros > 0 ? (
                <span>
                  {t('salary', { ns: 'actions' })}:{' '}
                  <span className="font-bold">{expectedSalaryEuros} EUR</span>
                </span>
              ) : null}
              {remoteWorkType ? (
                <span>
                  {t('workFromHome', { ns: 'actions' })}: {remoteWorkType}
                </span>
              ) : null}
              {employmentStatus != null && (
                <p>
                  <span>{t('employmentStatus.title', { ns: 'enums' })}:</span>
                  <span className="ml-1">
                    {t(`employmentStatus.${employmentStatus}`, {
                      ns: 'enums'
                    })}
                  </span>
                </p>
              )}
            </div>
            {editable && (
              <SectionEditButton
                data-cy="job-offer-preferences-modal"
                isPlain
                onClick={onEditPreferences}
              />
            )}
          </div>
          {contact && !print && (
            <div className="mt-3 flex flex-wrap gap-2">
              {!!advertId && (
                <Button
                  disabled={isMatchRejected}
                  onClick={() => {
                    if (candidateId && (router.query.job || advertId)) {
                      mutate({
                        candidateId,
                        advertId: advertId ?? (router.query.job as string),
                        advertStatus: AdvertStatus.Rejected
                      })
                    }
                  }}
                >
                  <IconClose />
                  <span className="ml-2">
                    {t(isMatchRejected ? 'project.rejected' : 'reject', {
                      ns: isMatchRejected ? 'crm' : 'actions'
                    })}
                  </span>
                </Button>
              )}
              {advertId && candidateId && (
                <ButtonWithIcon
                  isLoading={isLoading}
                  disabled={isSaved}
                  onClick={() => {
                    saveCv({
                      candidateId: candidateId ?? '',
                      advertId: advertId
                    })
                  }}
                  icon={IconArrow}
                >
                  <span className="ml-2">
                    {isSaved
                      ? t('project.cvSaved', { ns: 'crm' })
                      : t('project.cvSave', { ns: 'crm' })}
                  </span>
                </ButtonWithIcon>
              )}
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <Menu.Button as="div">
                      <Button>
                        <span>{t('actions:contact')}</span>
                        <IconChevron
                          className={clsx(
                            'ml-2 h-6 w-6 transition-all duration-300',
                            open ? 'rotate-180' : 'rotate-0'
                          )}
                        />
                      </Button>
                    </Menu.Button>
                    <Transition
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="min-w-60 absolute left-0 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <Menu.Item>
                          <button
                            className="my-3 flex w-full items-center border-none px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-100"
                            onClick={() => {
                              if (candidateId) {
                                setOpenChatConfig({
                                  val: candidateId,
                                  isModal: !redirectToMessage
                                })

                                if (redirectToMessage) {
                                  router.push('/org/messages')
                                }
                              }
                            }}
                          >
                            <span>
                              <IconMessages className="h-6 w-6" />
                            </span>
                            <span className="ml-2 opacity-75">
                              {t('sendMessage', { ns: 'actions' })}
                            </span>
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="my-3 flex w-full items-center border-none px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
                            disabled
                          >
                            <span>
                              <IconCalendar className="h-5 w-5" />
                            </span>
                            <span className="ml-2 opacity-75">
                              {t('suggestMeeting', { ns: 'actions' })}
                            </span>
                          </button>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </>
                )}
              </Menu>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CVHeader
